import { mapValues, urlQueryCodec, z, type DeepReadonly } from "@repo/utils";
import { untrack } from "svelte";

let raw = $state(window.location.href);
const url = $derived(new URL(raw));

export function currentRelativeURL(): string {
    return window.location.pathname + window.location.search + window.location.hash;
}

let listeners = 0;

export function useURL<
    S extends Record<string, z.core.$ZodType<unknown, string | string[]>>,
    Q = {
        [K in keyof z.output<z.ZodMiniObject<S>>]?: DeepReadonly<z.output<z.ZodMiniObject<S>>[K]>;
    },
>(shape?: S) {
    $effect(() => {
        function update() {
            raw = window.location.href;
        }
        const { pushState, replaceState } = history;
        if (listeners === 0) {
            window.addEventListener("popstate", update);
            history.pushState = function (...args) {
                pushState.apply(this, args);
                update();
            };
            history.replaceState = function (...args) {
                replaceState.apply(this, args);
                update();
            };
            update();
        }
        listeners += 1;
        return () => {
            listeners -= 1;
            if (listeners === 0) {
                history.replaceState = replaceState;
                history.pushState = pushState;
                window.removeEventListener("popstate", update);
            }
        };
    });

    let query = {} as Q;
    if (shape) {
        const codec = urlQueryCodec(z.object(mapValues(shape, (_, v) => z.optional(z.catch(v, undefined)))));
        const decoded = $derived(z.decode(codec, url.searchParams));
        query = new Proxy(
            {},
            {
                get(_, k: string) {
                    return decoded[k];
                },
                set(_, k: string, value) {
                    const next = new URL(untrack(() => url));
                    if (value === undefined) {
                        next.searchParams.delete(k);
                    } else {
                        next.search = z.encode(codec, { ...untrack(() => decoded), [k]: value }).toString();
                    }
                    window.history.replaceState(null, "", next);
                    return true;
                },
                deleteProperty(_, k: string) {
                    const next = new URL(untrack(() => url));
                    next.searchParams.delete(k);
                    window.history.replaceState(null, "", next);
                    return true;
                },
            },
        ) as Q;
    }
    return {
        get raw() {
            return raw;
        },
        get current() {
            return url;
        },
        get query() {
            return query;
        },
    };
}
